import React, { useState, useEffect } from 'react';
import {
  Card,
  Page,
  Layout,
  Button,
  DataTable,
  Badge,
  Modal,
  TextField,
  FormLayout,
  Toast,
  Frame,
  Loading,
  EmptyState,
  Select,
  LegacyStack as Stack,
  TextContainer,
  ButtonGroup,
  Banner,
  Tabs,
  ProgressBar,
} from '@shopify/polaris';
import { PlusMinor } from '@shopify/polaris-icons';

type FlowCategory = 'SIGN_UP' | 'SIGN_IN' | 'APPOINTMENT' | 'LEAD_GEN' | 'CONTACT_US' | 'CUSTOMER_SUPPORT' | 'SURVEY' | 'OTHER';
type FlowStatus = 'DRAFT' | 'PUBLISHED' | 'DEPRECATED';

interface Flow {
  id: string;
  whatsappFlowId: string;
  name: string;
  description?: string;
  category: FlowCategory;
  status: FlowStatus;
  flowData: any;
  endpointUrl?: string;
  responseCount?: number;
  completionRate?: number;
  createdAt: string;
  publishedAt?: string;
}

interface FlowResponse {
  id: string;
  flowId: string;
  conversationId: string;
  customer: {
    name: string;
    phone: string;
  };
  status: 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  responseData: any;
  createdAt: string;
  completedAt?: string;
}

interface FlowAnalytics {
  totalResponses: number;
  completedResponses: number;
  abandonedResponses: number;
  completionRate: number;
  averageCompletionTime: number;
}

interface FlowBuilderProps {
  storeId: string;
  onError?: (error: string) => void;
}

export const FlowBuilder: React.FC<FlowBuilderProps> = ({ storeId, onError }) => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [flowResponses, setFlowResponses] = useState<FlowResponse[]>([]);
  const [flowAnalytics, setFlowAnalytics] = useState<FlowAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // Modal states
  const [createModalActive, setCreateModalActive] = useState(false);
  const [viewResponsesModalActive, setViewResponsesModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'LEAD_GEN' as FlowCategory,
    whatsappFlowId: '',
    endpointUrl: '',
    endpointToken: '',
    flowData: '{}',
  });

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  const categoryOptions = [
    { label: 'Sign Up', value: 'SIGN_UP' },
    { label: 'Sign In', value: 'SIGN_IN' },
    { label: 'Appointment Booking', value: 'APPOINTMENT' },
    { label: 'Lead Generation', value: 'LEAD_GEN' },
    { label: 'Contact Us', value: 'CONTACT_US' },
    { label: 'Customer Support', value: 'CUSTOMER_SUPPORT' },
    { label: 'Survey', value: 'SURVEY' },
    { label: 'Other', value: 'OTHER' },
  ];

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/flows', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load flows');

      const data = await response.json();
      setFlows(data.data.flows || []);
    } catch (error: any) {
      showToast(error.message || 'Failed to load flows', true);
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFlowResponses = async (flowId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/flows/${flowId}/responses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load responses');

      const data = await response.json();
      setFlowResponses(data.data.responses || []);
    } catch (error: any) {
      showToast(error.message || 'Failed to load responses', true);
    } finally {
      setLoading(false);
    }
  };

  const loadFlowAnalytics = async (flowId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/flows/${flowId}/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load analytics');

      const data = await response.json();
      setFlowAnalytics(data.data);
    } catch (error: any) {
      showToast(error.message || 'Failed to load analytics', true);
    } finally {
      setLoading(false);
    }
  };

  const createFlow = async () => {
    if (!formData.name.trim() || !formData.whatsappFlowId.trim()) {
      showToast('Please enter flow name and WhatsApp Flow ID', true);
      return;
    }

    let parsedFlowData;
    try {
      parsedFlowData = JSON.parse(formData.flowData);
    } catch (error) {
      showToast('Invalid JSON in Flow Data', true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/flows', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          category: formData.category,
          whatsappFlowId: formData.whatsappFlowId,
          endpointUrl: formData.endpointUrl || undefined,
          endpointToken: formData.endpointToken || undefined,
          flowData: parsedFlowData,
        }),
      });

      if (!response.ok) throw new Error('Failed to create flow');

      showToast('Flow created successfully');
      setCreateModalActive(false);
      resetForm();
      loadFlows();
    } catch (error: any) {
      showToast(error.message || 'Failed to create flow', true);
    } finally {
      setLoading(false);
    }
  };

  const updateFlowStatus = async (flowId: string, status: FlowStatus) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/flows/${flowId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to update flow');

      showToast(`Flow ${status.toLowerCase()}`);
      loadFlows();
    } catch (error: any) {
      showToast(error.message || 'Failed to update flow', true);
    } finally {
      setLoading(false);
    }
  };

  const deleteFlow = async () => {
    if (!flowToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/flows/${flowToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete flow');

      showToast('Flow deleted successfully');
      setDeleteModalActive(false);
      setFlowToDelete(null);
      loadFlows();
    } catch (error: any) {
      showToast(error.message || 'Failed to delete flow', true);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'LEAD_GEN',
      whatsappFlowId: '',
      endpointUrl: '',
      endpointToken: '',
      flowData: '{}',
    });
  };

  const showToast = (message: string, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const getBadgeStatus = (status: string): 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'info';
      case 'DEPRECATED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getResponseBadgeStatus = (status: string): 'success' | 'warning' | 'info' | 'attention' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'STARTED':
        return 'attention';
      case 'ABANDONED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const flowRows = flows.map((flow) => [
    flow.name,
    <Badge>{flow.category.replace('_', ' ')}</Badge>,
    <Badge status={getBadgeStatus(flow.status)}>{flow.status}</Badge>,
    flow.responseCount || 0,
    flow.completionRate ? `${flow.completionRate.toFixed(1)}%` : 'N/A',
    flow.publishedAt
      ? new Date(flow.publishedAt).toLocaleDateString()
      : 'Not published',
    <ButtonGroup>
      <Button
        size="slim"
        onClick={() => {
          setSelectedFlow(flow);
          loadFlowResponses(flow.id);
          loadFlowAnalytics(flow.id);
          setViewResponsesModalActive(true);
        }}
      >
        View Responses
      </Button>
      {flow.status === 'DRAFT' && (
        <Button
          size="slim"
          primary
          onClick={() => updateFlowStatus(flow.id, 'PUBLISHED')}
        >
          Publish
        </Button>
      )}
      {flow.status === 'PUBLISHED' && (
        <Button
          size="slim"
          onClick={() => updateFlowStatus(flow.id, 'DEPRECATED')}
        >
          Deprecate
        </Button>
      )}
      <Button
        size="slim"
        destructive
        onClick={() => {
          setFlowToDelete(flow);
          setDeleteModalActive(true);
        }}
      >
        Delete
      </Button>
    </ButtonGroup>,
  ]);

  const responseRows = flowResponses.map((response) => [
    response.customer.name || response.customer.phone,
    response.customer.phone,
    <Badge status={getResponseBadgeStatus(response.status)}>{response.status}</Badge>,
    new Date(response.createdAt).toLocaleString(),
    response.completedAt
      ? new Date(response.completedAt).toLocaleString()
      : 'N/A',
    <Button
      size="slim"
      onClick={() => {
        alert(JSON.stringify(response.responseData, null, 2));
      }}
    >
      View Data
    </Button>,
  ]);

  const tabs = [
    {
      id: 'responses',
      content: 'Responses',
      panelID: 'responses-panel',
    },
    {
      id: 'analytics',
      content: 'Analytics',
      panelID: 'analytics-panel',
    },
  ];

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastActive(false)}
      duration={3000}
    />
  ) : null;

  return (
    <Frame>
      <Page
        title="WhatsApp Flows"
        primaryAction={{
          content: 'Create Flow',
          icon: PlusMinor,
          onAction: () => setCreateModalActive(true),
        }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              {loading && flows.length === 0 ? (
                <Loading />
              ) : flows.length === 0 ? (
                <EmptyState
                  heading="No flows yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Create Flow',
                    onAction: () => setCreateModalActive(true),
                  }}
                >
                  <p>
                    Create interactive forms to collect information from customers in
                    WhatsApp.
                  </p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'numeric',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Name',
                    'Category',
                    'Status',
                    'Responses',
                    'Completion Rate',
                    'Published',
                    'Actions',
                  ]}
                  rows={flowRows}
                />
              )}
            </Card>
          </Layout.Section>

          <Layout.Section secondary>
            <Card title="About Flows" sectioned>
              <TextContainer>
                <p>
                  WhatsApp Flows are interactive forms that collect structured data from
                  customers within WhatsApp.
                </p>
                <p>
                  Use flows for lead generation, surveys, appointment booking, and more.
                </p>
              </TextContainer>
            </Card>

            <Card title="Getting Started" sectioned>
              <TextContainer>
                <ol>
                  <li>Create a flow in WhatsApp Flow Builder</li>
                  <li>Copy the Flow ID from Meta Business</li>
                  <li>Create the flow here with that ID</li>
                  <li>Publish the flow</li>
                  <li>Send flow messages to customers</li>
                </ol>
              </TextContainer>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Create Flow Modal */}
        <Modal
          open={createModalActive}
          onClose={() => setCreateModalActive(false)}
          title="Create WhatsApp Flow"
          primaryAction={{
            content: 'Create',
            onAction: createFlow,
            loading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setCreateModalActive(false);
                resetForm();
              },
            },
          ]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label="Flow Name"
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                placeholder="Lead Generation Form"
                autoComplete="off"
              />

              <TextField
                label="Description (optional)"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Collect customer information"
                multiline={2}
                autoComplete="off"
              />

              <Select
                label="Category"
                options={categoryOptions}
                value={formData.category}
                onChange={(value) =>
                  setFormData({ ...formData, category: value as FlowCategory })
                }
              />

              <TextField
                label="WhatsApp Flow ID"
                value={formData.whatsappFlowId}
                onChange={(value) => setFormData({ ...formData, whatsappFlowId: value })}
                placeholder="123456789012345"
                helpText="Get this from WhatsApp Flow Builder in Meta Business Manager"
                autoComplete="off"
              />

              <TextField
                label="Endpoint URL (optional)"
                value={formData.endpointUrl}
                onChange={(value) => setFormData({ ...formData, endpointUrl: value })}
                placeholder="https://your-domain.com/flow-endpoint"
                type="url"
                autoComplete="off"
              />

              <TextField
                label="Endpoint Token (optional)"
                value={formData.endpointToken}
                onChange={(value) => setFormData({ ...formData, endpointToken: value })}
                placeholder="your-secret-token"
                type="password"
                autoComplete="off"
              />

              <TextField
                label="Flow Data (JSON)"
                value={formData.flowData}
                onChange={(value) => setFormData({ ...formData, flowData: value })}
                placeholder='{"screen": "welcome", "data": {}}'
                multiline={4}
                helpText="Initial data to pass to the flow"
                autoComplete="off"
              />

              <Banner status="info">
                <p>
                  First create your flow in WhatsApp Flow Builder, then add it here with
                  the Flow ID. You can find the Flow ID in your Meta Business Manager.
                </p>
              </Banner>
            </FormLayout>
          </Modal.Section>
        </Modal>

        {/* View Responses Modal */}
        <Modal
          large
          open={viewResponsesModalActive}
          onClose={() => setViewResponsesModalActive(false)}
          title={`${selectedFlow?.name} - Data & Analytics`}
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setViewResponsesModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              {selectedTab === 0 && (
                <Card.Section>
                  {loading ? (
                    <Loading />
                  ) : flowResponses.length === 0 ? (
                    <EmptyState
                      heading="No responses yet"
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                      <p>Responses will appear here when customers submit the flow.</p>
                    </EmptyState>
                  ) : (
                    <DataTable
                      columnContentTypes={[
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                      ]}
                      headings={[
                        'Customer',
                        'Phone',
                        'Status',
                        'Started',
                        'Completed',
                        'Data',
                      ]}
                      rows={responseRows}
                    />
                  )}
                </Card.Section>
              )}

              {selectedTab === 1 && (
                <Card.Section>
                  {loading ? (
                    <Loading />
                  ) : flowAnalytics ? (
                    <Stack vertical>
                      <TextContainer>
                        <p>
                          <strong>Total Responses:</strong> {flowAnalytics.totalResponses}
                        </p>
                        <p>
                          <strong>Completed:</strong> {flowAnalytics.completedResponses}
                        </p>
                        <p>
                          <strong>Abandoned:</strong> {flowAnalytics.abandonedResponses}
                        </p>
                        <p>
                          <strong>Completion Rate:</strong>{' '}
                          {flowAnalytics.completionRate.toFixed(1)}%
                        </p>
                        {flowAnalytics.averageCompletionTime > 0 && (
                          <p>
                            <strong>Avg. Completion Time:</strong>{' '}
                            {Math.round(flowAnalytics.averageCompletionTime / 1000)} seconds
                          </p>
                        )}
                      </TextContainer>

                      <div style={{ marginTop: '1rem' }}>
                        <p style={{ marginBottom: '0.5rem' }}>
                          <strong>Completion Rate</strong>
                        </p>
                        <ProgressBar progress={flowAnalytics.completionRate} />
                      </div>
                    </Stack>
                  ) : (
                    <EmptyState heading="No analytics available">
                      <p>Analytics will be available once responses are collected.</p>
                    </EmptyState>
                  )}
                </Card.Section>
              )}
            </Tabs>
          </Modal.Section>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalActive}
          onClose={() => setDeleteModalActive(false)}
          title="Delete Flow"
          primaryAction={{
            content: 'Delete',
            onAction: deleteFlow,
            loading,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setDeleteModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <TextContainer>
              <p>
                Are you sure you want to delete the flow "{flowToDelete?.name}"?
              </p>
              <p>
                This will remove the flow and all associated responses. This action
                cannot be undone.
              </p>
            </TextContainer>
          </Modal.Section>
        </Modal>

        {toastMarkup}
      </Page>
    </Frame>
  );
};
